import { ModuleColumnDefinition, ModuleTypeEnum } from './module'

export enum ImportExportFormatEnum {
  CSV = 'CSV',
  JSON = 'JSON',
  EXCEL = 'EXCEL',
}

export enum ImportActionEnum {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  SKIP = 'SKIP',
  AMBIGUOUS = 'AMBIGUOUS',
}

export interface ImportFieldMapping {
  sourceField: string
  targetField: string
  isRequired: boolean
  fieldType?: string
}

export interface DuplicateMatch {
  id: string
  displayLabel: string
  matchedField: string
  distinguishingInfo?: string
  record: any
}

export interface ImportPreviewRecord {
  rowIndex: number
  data: Record<string, any>
  action: ImportActionEnum
  existingRecord?: any
  errors?: string[]
  warnings?: string[]
  duplicateMatches?: DuplicateMatch[]
}

export interface ImportPreviewResult {
  total: number
  toCreate: number
  toUpdate: number
  toSkip: number
  toAmbiguous: number
  errors: number
  records: ImportPreviewRecord[]
  fieldMappings: ImportFieldMapping[]
}

export interface ImportFieldMappings {
  statusMappings?: Record<string, string> // source status/lifecycle -> target status/lifecycle
  assigneeMappings?: Record<string, string> // source assignee -> target user ID
  priorityMappings?: Record<string, string> // source priority -> target priority
  sprintMappings?: Record<string, string> // source sprint -> target sprint ID
  taskGroupMappings?: Record<string, string> // source task group/epic -> target group ID
  stageMappings?: Record<string, string> // source stage -> target stage ID (leads/deals)
  entityMappings?: Record<string, string> // source entity name -> target entity ID (or __CREATE_NEW__)
  supplierMappings?: Record<string, string> // source supplier name -> target supplier ID (or __CREATE_NEW__)
  leadMappings?: Record<string, string> // source lead name -> target lead ID
  projectTypeMappings?: Record<string, string> // source project type -> target type (BASIC/KANBAN/SCRUM/WATERFALL)
  entityTypeMappings?: Record<string, string> // source entity type -> target entity type ID
  tagsMappings?: Record<string, string> // source tag name -> target tag ID (or __CREATE_NEW__::tagName)
  customFieldMappings?: Record<string, string> // source field -> target field
  customFieldValueMappings?: Record<string, Record<string, string>> // fieldName -> { sourceValue -> targetValue }
}

/**
 * A decision the user made in the Data Review step about a single file row.
 *
 * The synchronous import path posts the reviewed rows themselves, so it carries
 * these decisions implicitly. The job path only uploads the file and re-derives
 * the preview server-side, which is why the choice has to travel separately,
 * keyed by row index (the same channel `rowContextMap` already uses).
 *
 * `existingRecordId` is the id of the chosen `DuplicateMatch`. The processor
 * verifies it is still one of that row's candidates before applying it, so a
 * stale or misaligned resolution degrades to a skip rather than overwriting the
 * wrong record.
 */
export interface ImportRowResolution {
  action:
    | ImportActionEnum.CREATE
    | ImportActionEnum.UPDATE
    | ImportActionEnum.SKIP
  existingRecordId?: string
}

export interface ImportOptions {
  moduleType: ModuleTypeEnum
  organizationId?: string
  userId?: string
  updateExisting?: boolean
  matchFields?: string[]
  customFieldMappings?: Record<string, string>
  useEntityTerminology?: boolean
  projectId?: string
  entityId?: string
  defaultTaskGroupId?: string
  collectionId?: string
  fieldMappings?: ImportFieldMappings
  rowContextMap?: Record<number, { projectId?: string; taskGroupId?: string }>
  // Per-row duplicate resolutions from the Data Review step, keyed by the row's
  // index in the parsed file. Only rows the user actually decided on appear.
  rowResolutions?: Record<number, ImportRowResolution>
  // T1 — capture provenance. Set by the import-job processor (one Capture per
  // import FILE); import services stamp it on every record they CREATE.
  // Never client-supplied.
  captureId?: string
  // Task import only. The job processor feeds the file in chunks, so a row may
  // legitimately name a parent that lives in a later chunk. With this set, an
  // unresolvable parent reference is handed back on `pendingParentLinks`
  // instead of failing the row, and the processor retries the whole set once
  // the last chunk has been written. Never client-supplied.
  deferParentLinks?: boolean
}

export interface ExportOptions {
  moduleType: ModuleTypeEnum
  format: ImportExportFormatEnum
  includeCustomFields?: boolean
  includeSystemFields?: boolean
  dateFormat?: string
  useEntityTerminology?: boolean
  filters?: Record<string, any>
  selectedIds?: string[]
  projectId?: string
  collectionId?: string
}

/**
 * A parent/subtask link an imported row asked for, held until the reference can
 * be resolved. Carries the row's own data so a link that never resolves can be
 * reported as an ordinary per-row failure.
 */
export interface PendingParentLink {
  rowIndex: number
  /** The task the row wrote — the child of the link. */
  taskId: string
  /** Whatever the file said: a task number, a task name, or a task id. */
  parentRef: string
  /** The child's module, so a name-based reference resolves in the right project. */
  moduleType?: string
  moduleId?: string
  /** A task this run created cannot already have subtasks — lets the retry skip a lookup. */
  createdInRun: boolean
  data: Record<string, any>
}

export interface ImportResult {
  success: boolean
  created: number
  updated: number
  skipped: number
  errors: number
  createdIds?: string[]
  updatedIds?: string[]
  /** Maps assignee user ID → list of task names assigned during import (excludes the importer) */
  assigneeTaskMap?: Record<string, string[]>
  /**
   * Parent/subtask links this run wrote a task for but could not resolve yet.
   * Only populated when the caller set `deferParentLinks` (the chunked job
   * path); it owns retrying them once every chunk is in.
   */
  pendingParentLinks?: PendingParentLink[]
  details?: {
    createdRecords?: any[]
    updatedRecords?: any[]
    errorRecords?: Array<{
      rowIndex: number
      errors: string[]
      data: Record<string, any>
    }>
  }
}

export interface CSVTemplateField {
  fieldName: string
  displayName: string
  isRequired: boolean
  fieldType: string
  description?: string
  example?: string
  options?: string[]
}

export interface CSVTemplate {
  moduleType: ModuleTypeEnum
  fields: CSVTemplateField[]
  instructions?: string
}

export interface ImportWizardState {
  currentStep: ImportWizardStep
  contextProjectId?: string
  contextEntityId?: string
  contextTaskGroupId?: string
  file?: File
  fileDocumentId?: string
  csvHeaders?: string[]
  sampleRecords?: Record<string, unknown>[]
  totalRecords?: number
  columnExamples?: Record<string, string[]>
  customColumns?: ModuleColumnDefinition[]
  previewSummary?: {
    total: number
    toCreate: number
    toUpdate: number
    toSkip: number
    toAmbiguous: number
    errors: number
    warnings: number
  }
  importResult?: ImportResult
  importErrorMessage?: string
  previewRecords: ImportPreviewRecord[]
  fieldMappings: ImportFieldMapping[]
  importFieldMappings: ImportFieldMappings
  selectedRecords: Set<number>
  uniqueStatuses: string[]
  uniqueAssignees: string[]
  uniquePriorities: string[]
  uniqueSprints: string[]
  uniqueTaskGroups: string[]
  matchFields?: string[]
  updateExisting?: boolean
  rowContextMap?: Record<number, { projectId?: string; taskGroupId?: string }>
  // What the user chose for each contested row in Data Review. The rows in
  // previewRecords are mutated in place for the synchronous path; this map is
  // what survives into a background job.
  rowResolutions?: Record<number, ImportRowResolution>
  valueMappingCategories?: string[] // 'status' also covers contact/entity lifecycle mappings
}

export enum ImportWizardStep {
  FILE_UPLOAD = 'file_upload',
  CONTEXT_SELECTION = 'context_selection',
  FIELD_MAPPING = 'field_mapping',
  VALUE_MAPPING = 'value_mapping',
  DATA_REVIEW = 'data_review',
  IMPORT_COMPLETE = 'import_complete',
}

export interface StatusMapping {
  sourceStatus: string
  targetStatus: string
  isRequired: boolean
  recordCount: number
  statusContext?: string // Context about which statuses are being used (e.g., task group name)
}

export interface AssigneeMapping {
  sourceAssignee: string
  targetUserId: string | null
  targetUserName: string | null
  isMatched: boolean
  recordCount: number
}

export interface PriorityMapping {
  sourcePriority: string
  targetPriority: string
  isValid: boolean
  recordCount: number
}

export interface SprintMapping {
  sourceSprint: string
  targetSprintId: string
  targetSprintName: string
  isExisting: boolean // true if mapping to existing sprint, false if will create new
  recordCount: number
}

export interface TaskGroupMapping {
  sourceTaskGroup: string
  targetTaskGroupId: string
  targetTaskGroupName: string
  isExisting: boolean // true if mapping to existing group, false if will create new
  recordCount: number
}

export interface CustomFieldValueMapping {
  sourceValue: string
  targetValue: string
  recordCount: number
}

export enum ImportJobStatusEnum {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export interface ImportJob {
  id: string
  organizationId: string
  moduleType: ModuleTypeEnum
  status: ImportJobStatusEnum
  fileDocumentId?: string
  fileName?: string
  totalRecords: number
  processedRecords: number
  createdCount: number
  updatedCount: number
  skippedCount: number
  errorCount: number
  errorMessage?: string
  /** Per-row failure detail persisted with the job (capped; errorCount is exact) */
  failedRecords?: Array<{
    rowIndex: number
    errors: string[]
    data: Record<string, any>
  }>
  createdBy?: string
  startedAt?: string
  completedAt?: string
  createdAt?: string
  updatedAt?: string
}

export interface CreateImportJobRequest {
  fileDocumentId: string
  totalRecords?: number
  fieldMappings: ImportFieldMapping[]
  options: ImportOptions
}

export interface ImportFileUploadResponse {
  documentId: string
  fileName: string
  contentType: string
  fileSize: number
  storageKey?: string
}
